/**
 * WorkshopMemberDiscountBanner — shown to Thrive members whose monthly workshop
 * credit has already been used this month. Communicates that the 30% member
 * discount is applied and shows the reset date so she can plan next month.
 */
import { motion } from 'framer-motion';
import { Tag, Calendar, Sparkles } from 'lucide-react';

const MEMBER_DISCOUNT_PCT = 30;

function formatResetDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  } catch { return iso; }
}

export default function WorkshopMemberDiscountBanner({ basePriceCents, nextResetIso }) {
  const base = basePriceCents / 100;
  const discounted = base * (100 - MEMBER_DISCOUNT_PCT) / 100;
  const savings = base - discounted;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="rounded-2xl border border-teal/25 bg-gradient-to-br from-cream to-seafoam/30 p-5 mb-6"
      data-testid="workshop-member-discount-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-teal/20 flex items-center justify-center shrink-0">
          <Tag className="text-teal-deep" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] tracking-[0.18em] uppercase text-teal-deep font-semibold mb-1">
            Thrive member benefit applied
          </p>
          <h3 className="font-display text-xl text-navy mb-1">
            {MEMBER_DISCOUNT_PCT}% off — ${discounted.toFixed(2)}{' '}
            <span className="text-text-mid text-base line-through ml-1 font-sans">${base.toFixed(2)}</span>
          </h3>
          <p className="text-sm text-text-mid">
            You&apos;ve used this month&apos;s included workshop. You save <strong className="text-teal-deep">${savings.toFixed(2)}</strong> on this one.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-navy bg-white/70 rounded-full px-3 py-1.5 border border-border-subtle">
            <Calendar size={12} className="text-teal-deep" />
            <span>Next included workshop credit unlocks {formatResetDate(nextResetIso)}</span>
            <Sparkles size={12} className="text-teal" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
